import React, { useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LayoutDashboard,
  ShoppingBag,
  BookOpen,
  Users as UsersIcon,
  AlertTriangle,
  Activity,
  ScrollText,
  Sliders,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Wrench,
  CreditCard,
  Trophy,
  MapPin,
  Megaphone,
} from 'lucide-react';
import './AdminLayout.css';

const NAV = [
  { to: '/admin/overview',               icon: LayoutDashboard, key: 'overview',  en: 'Overview',       ar: 'نظرة عامة' },
  { to: '/admin/ads',                    icon: ShoppingBag,     key: 'ads',       en: 'Ads',            ar: 'الإعلانات' },
  { to: '/admin/books',                  icon: BookOpen,        key: 'books',     en: 'Books',          ar: 'الكتب' },
  { to: '/admin/users',                  icon: UsersIcon,       key: 'users',     en: 'Users',          ar: 'المستخدمون' },
  { to: '/admin/reports',                icon: AlertTriangle,   key: 'reports',   en: 'Reports',        ar: 'البلاغات' },
  // iter297 — Phase 3 Part 2: unified manual payments admin view.
  { to: '/admin/payments',               icon: CreditCard,      key: 'payments',  en: 'Payments',       ar: 'المدفوعات' },
  // iter378 (v14) — Direct sponsorship campaign manager.
  { to: '/admin/sponsorships',           icon: Megaphone,       key: 'sponsorships', en: 'Sponsorships', ar: 'الرعايات الإعلانية' },
  // iter305 — Seller success story approval queue (+10 coins on approve).
  { to: '/admin/seller-stories',         icon: Trophy,          key: 'stories',   en: 'Seller Stories', ar: 'قصص البائعين' },
  { to: '/admin/system-health',          icon: Activity,        key: 'system',    en: 'System Health',  ar: 'صحة النظام' },
  // iter322 Phase C — location-completeness metrics for the marketplace
  { to: '/admin/location-quality',       icon: MapPin,          key: 'locqual',   en: 'Location Quality', ar: 'جودة الموقع' },
  { to: '/admin/cleanup-legacy-images',  icon: Wrench,          key: 'cleanup',   en: 'Image Cleanup',  ar: 'تنظيف الصور' },
  { to: '/admin/audit-logs',             icon: ScrollText,      key: 'audit',     en: 'Audit Logs',     ar: 'سجل المراجعة' },
  // iter295 — Phase 3: payment kill-switch lives here.
  { to: '/admin/settings',               icon: Sliders,         key: 'settings',  en: 'Settings',       ar: 'الإعدادات' },
];

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = useMemo(() => {
    if (!user) return false;
    if (user.is_admin) return true;
    return ['admin', 'super_admin', 'moderator', 'support', 'finance_admin'].includes(user.admin_role);
  }, [user]);

  if (loading) return null;
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className={`admin-shell ${isArabic ? 'rtl' : 'ltr'}`}
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="admin-shell"
    >
      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="admin-brand">
          <ShieldCheck size={20} />
          <span>{isArabic ? 'لوحة المشرف' : 'Admin Console'}</span>
        </div>
        <nav className="admin-nav">
          {NAV.map(({ to, icon: Icon, key, en, ar }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `admin-nav-link${isActive ? ' active' : ''}`}
              data-testid={`admin-nav-${key}`}
            >
              <Icon size={18} />
              <span>{isArabic ? ar : en}</span>
            </NavLink>
          ))}
        </nav>
        <div className="admin-side-footer">
          <button
            type="button"
            className="admin-back-btn"
            onClick={() => navigate('/')}
            data-testid="admin-back-to-app"
          >
            {isArabic ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
            <span>{isArabic ? 'عودة للتطبيق' : 'Back to app'}</span>
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar" data-testid="admin-topbar">
          <div className="admin-topbar-left">
            <span className="admin-topbar-scope" data-testid="admin-scope-badge">
              {isArabic ? 'نطاق المنصة' : 'Platform scope'}
            </span>
          </div>
          <div className="admin-topbar-right">
            <span className="admin-actor-role" data-testid="admin-actor-role">
              {user?.admin_role || (user?.is_admin ? 'admin' : '')}
            </span>
            <span className="admin-actor-name" data-testid="admin-actor-name">
              {user?.name || user?.email}
            </span>
          </div>
        </header>
        <main className="admin-content" data-testid="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
